import { useState } from "react";
import { Link } from "react-router-dom";
import type { Abi, Address } from "viem";
import { useQueryClient } from "@tanstack/react-query";
import { houseVaultAbi, strikeExchangeAbi, strikeOptionsAbi, strikeRouterAbi } from "../abi";
import {
  orderTuple,
  requestSellQuote,
  useApiConfig,
  useMarket,
  useOptionChain,
  usePositions,
  useUserOrders,
  type ChainSeries,
  type SeriesRow,
  type UserOrder,
} from "../lib/api";
import { fmtEth, fmtMcap, fmtNum, timeLeft, wei } from "../lib/format";
import { connectedAccount, sendTx, simulate } from "../lib/tx";
import { friendlyError } from "../lib/errors";
import { toast } from "../lib/toast";
import { ConnectButton } from "../components/Connect";
import { useWallet } from "../lib/wallet";
import "./portfolio.css";

export function Portfolio() {
  const conn = useWallet();
  const { data: pos, isLoading } = usePositions(conn.address);
  const { data: m } = useMarket();
  const { data: cfg } = useApiConfig();
  const { data: chain } = useOptionChain();
  const qc = useQueryClient();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["positions"] });
    qc.invalidateQueries({ queryKey: ["chain"] });
  };
  const bids = new Map((chain?.series ?? []).map((c) => [c.id, c]));

  if (!conn.isConnected) {
    return (
      <section className="wrap page">
        <PageHead />
        <div className="panel empty">
          <p>Connect your wallet to see your options.</p>
          <ConnectButton />
        </div>
      </section>
    );
  }

  const now = Date.now() / 1000;
  const longs = (pos?.longs ?? []).filter((l) => l.series && BigInt(l.amount) > 0n);
  const live = longs.filter((l) => l.series.expiry > now);
  const expired = longs.filter((l) => l.series.expiry <= now);
  const spot = m?.priceEth ?? 0;
  const eu = m?.ethUsd ?? 0;

  async function toggleAuto() {
    if (!cfg || !pos) return;
    if (!pos.routerApproved) await approveRouter(cfg.contracts.options, cfg.contracts.router);
    await sendTx(pos.autoExercise ? "Turn off auto-exercise" : "Turn on auto-exercise", {
      address: cfg.contracts.router,
      abi: strikeRouterAbi,
      functionName: "setAutoExercise",
      args: [!pos.autoExercise],
    });
    refresh();
  }

  return (
    <section className="wrap page">
      <PageHead />

      <div className="panel">
        <div className="panel-head">
          <h3>Your options</h3>
          <button className="auto-toggle" onClick={toggleAuto} disabled={!pos}>
            <span className={`switch ${pos?.autoExercise ? "on" : ""}`} />
            <span className="mono">Auto-exercise</span>
          </button>
        </div>
        {isLoading ? (
          <div className="empty">Loading…</div>
        ) : live.length === 0 ? (
          <div className="empty">
            <p>No open options.</p>
            <Link to="/trade" className="btn btn-primary">
              Open the option chain
            </Link>
          </div>
        ) : (
          <div className="rows">
            {live.map((l) => (
              <LongRow
                key={l.series.id}
                s={l.series}
                amount={BigInt(l.amount)}
                spot={spot}
                ethUsd={eu}
                approved={!!pos?.routerApproved}
                bid={bids.get(l.series.id)}
                onDone={refresh}
              />
            ))}
          </div>
        )}
        <p className="note rows-note">
          Sell returns any option to the options vault for ETH at its current price, any time before expiry. Cash out uses an
          in-the-money option and sells the result in the pool. Auto-exercise cashes out for you in the last hour before expiry,
          only if it leaves you a profit.
        </p>
      </div>

      {(pos?.shorts.length ?? 0) > 0 && (
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-head">
            <h3>Options you sold</h3>
            <span className="mono mute">collateral · earned from exercises</span>
          </div>
          <div className="rows">
            {pos!.shorts.map((sh) => (
              <ShortRow key={sh.series.id} s={sh.series} collateral={BigInt(sh.collateral)} proceeds={BigInt(sh.proceeds)} ethUsd={eu} onDone={refresh} />
            ))}
          </div>
        </div>
      )}

      <MyOrders maker={conn.address!} />

      {expired.length > 0 && (
        <details className="expired">
          <summary className="mono mute">{expired.length} expired</summary>
          {expired.map((l) => (
            <div key={l.series.id} className="kv">
              <span>
                {l.series.is_call ? "Call" : "Put"} {fmtMcap(wei(l.series.strike), eu)}
              </span>
              <span className="faint">{fmtNum(wei(l.amount))} expired</span>
            </div>
          ))}
        </details>
      )}
    </section>
  );
}

function PageHead() {
  return (
    <header className="page-head">
      <span className="eyebrow">Portfolio</span>
      <h1>Your positions.</h1>
    </header>
  );
}

// The router bubbles up the exchange's and the vault's errors; include them so they decode by name.
const sellAbi = [
  ...strikeRouterAbi,
  ...strikeExchangeAbi.filter((x) => x.type === "error"),
  ...houseVaultAbi.filter((x) => x.type === "error"),
] as Abi;

async function approveRouter(options: Address, router: Address) {
  await sendTx("Allow one-click sell and cash-out", {
    address: options,
    abi: strikeOptionsAbi,
    functionName: "setApprovalForAll",
    args: [router, true],
  });
}

function LongRow({
  s,
  amount,
  spot,
  ethUsd,
  approved,
  bid,
  onDone,
}: {
  s: SeriesRow;
  amount: bigint;
  spot: number;
  ethUsd: number;
  approved: boolean;
  bid?: ChainSeries;
  onDone: () => void;
}) {
  const { data: cfg } = useApiConfig();
  const [busy, setBusy] = useState<"" | "sell" | "cash">("");
  const isCall = s.is_call === 1;
  const strike = wei(s.strike);
  const n = wei(amount);
  const intrinsic = (isCall ? Math.max(0, spot - strike) : Math.max(0, strike - spot)) * n;
  const itm = intrinsic > 0;
  // The vault buys back up to its own open position on the series; the seller gets 90% after the fee.
  const bidCap = BigInt(bid?.vaultBidCapacity ?? "0");
  const sellAmount = amount < bidCap ? amount : bidCap;
  const sellValue = (bid?.bidEth ?? 0) * wei(sellAmount) * 0.9;
  const canSell = sellValue > 0;

  async function sell() {
    if (!cfg) return;
    setBusy("sell");
    try {
      if (!approved) await approveRouter(cfg.contracts.options, cfg.contracts.router);
      let q;
      try {
        q = await requestSellQuote(s.id, sellAmount);
      } catch (err) {
        toast.show("err", friendlyError(err));
        return;
      }
      const args = [orderTuple(q.order), q.signature, sellAmount, BigInt(q.proceeds)] as const;
      try {
        await simulate<bigint>({ address: cfg.contracts.router, abi: sellAbi, functionName: "sellForEth", args });
      } catch (err) {
        toast.show("err", friendlyError(err));
        return;
      }
      const what = `${fmtNum(wei(sellAmount))} ${isCall ? "calls" : "puts"}`;
      await sendTx(`Sell ${what} for ${fmtEth(wei(q.proceeds))}`, {
        address: cfg.contracts.router,
        abi: sellAbi,
        functionName: "sellForEth",
        args,
      });
      onDone();
    } catch {
      // sendTx already reported the error.
    } finally {
      setBusy("");
    }
  }

  async function cashOut() {
    if (!cfg) return;
    setBusy("cash");
    try {
      if (!approved) await approveRouter(cfg.contracts.options, cfg.contracts.router);
      const me = await connectedAccount();
      let profit: bigint;
      try {
        // Dry run: how much ETH the exercise + swap returns right now.
        profit = await simulate<bigint>({
          address: cfg.contracts.router,
          abi: strikeRouterAbi,
          functionName: "cashOut",
          args: [BigInt(s.id), amount, 1n, me],
        });
      } catch (err) {
        toast.show("err", friendlyError(err));
        return;
      }
      await sendTx(`Cash out ${isCall ? "calls" : "puts"} for ${fmtEth(wei(profit))}`, {
        address: cfg.contracts.router,
        abi: strikeRouterAbi,
        functionName: "cashOut",
        args: [BigInt(s.id), amount, (profit * 97n) / 100n, me], // 3% slippage guard
      });
      onDone();
    } catch {
      // sendTx already reported the error.
    } finally {
      setBusy("");
    }
  }

  return (
    <div className="row">
      <div className="row-main">
        <span className={`pill ${isCall ? "call" : "put"}`}>{isCall ? "Call" : "Put"}</span>
        <div>
          <div className="row-title">
            {isCall ? "Above" : "Below"} <span className="num">{fmtMcap(strike, ethUsd)}</span>
          </div>
          <div className="row-sub mono">
            {fmtNum(n)} options · expires in {timeLeft(s.expiry)}
          </div>
        </div>
      </div>
      <div className="row-value">
        {itm ? (
          <>
            <span className={`num ${isCall ? "call" : "put"}`}>≈ {fmtEth(intrinsic)}</span>
            <span className="row-sub mono">if cashed out now, before swap fees</span>
          </>
        ) : canSell ? (
          <>
            <span className="num">≈ {fmtEth(sellValue)}</span>
            <span className="row-sub mono">
              {sellAmount < amount ? `selling ${fmtNum(wei(sellAmount))} now` : "if sold now"}
            </span>
          </>
        ) : (
          <>
            <span className="num faint">Out of the money</span>
            <span className="row-sub mono">cash out when it's in the money</span>
          </>
        )}
      </div>
      <div className="row-actions">
        <button className="btn btn-ghost" disabled={!canSell || !!busy} onClick={sell} title={canSell ? "" : "No bid on this option right now"}>
          {busy === "sell" ? "Working…" : "Sell"}
        </button>
        <button className={`btn ${isCall ? "btn-primary" : "btn-put"}`} disabled={!itm || !!busy} onClick={cashOut}>
          {busy === "cash" ? "Working…" : "Cash out"}
        </button>
      </div>
    </div>
  );
}

function ShortRow({
  s,
  collateral,
  proceeds,
  ethUsd,
  onDone,
}: {
  s: SeriesRow;
  collateral: bigint;
  proceeds: bigint;
  ethUsd: number;
  onDone: () => void;
}) {
  const { data: cfg } = useApiConfig();
  const isCall = s.is_call === 1;
  const expired = s.expiry <= Date.now() / 1000;
  const collUnit = isCall ? "$STRIKE" : "WETH";
  const procUnit = isCall ? "WETH" : "$STRIKE";

  async function act(fn: "claimProceeds" | "redeem") {
    if (!cfg) return;
    const me = await connectedAccount();
    await sendTx(fn === "redeem" ? "Withdraw collateral" : "Claim earnings", {
      address: cfg.contracts.options,
      abi: strikeOptionsAbi,
      functionName: fn,
      args: [BigInt(s.id), me],
    });
    onDone();
  }

  return (
    <div className="row">
      <div className="row-main">
        <span className={`pill ${isCall ? "call" : "put"}`}>{isCall ? "Sold call" : "Sold put"}</span>
        <div>
          <div className="row-title">
            <span className="num">{fmtMcap(wei(s.strike), ethUsd)}</span>
          </div>
          <div className="row-sub mono">{expired ? "expired" : `expires in ${timeLeft(s.expiry)}`}</div>
        </div>
      </div>
      <div className="row-value">
        <span className="num">
          {fmtNum(wei(collateral))} {collUnit}
        </span>
        <span className="row-sub mono">
          {wei(proceeds) > 0 ? `+ ${fmtNum(wei(proceeds))} ${procUnit} earned` : "locked as collateral"}
        </span>
      </div>
      <div className="row-actions">
        {proceeds > 0n && (
          <button className="btn" onClick={() => act("claimProceeds")}>
            Claim
          </button>
        )}
        {expired && collateral > 0n && (
          <button className="btn btn-paper" onClick={() => act("redeem")}>
            Withdraw
          </button>
        )}
      </div>
    </div>
  );
}

function MyOrders({ maker }: { maker: Address }) {
  const { data } = useUserOrders();
  const { data: cfg } = useApiConfig();
  const mine = (data ?? []).filter((o) => o.maker.toLowerCase() === maker.toLowerCase());
  if (!mine.length) return null;

  async function cancel(o: UserOrder) {
    if (!cfg) return;
    await sendTx("Cancel order", {
      address: cfg.contracts.exchange,
      abi: strikeExchangeAbi,
      functionName: "cancel",
      args: [
        {
          maker: o.maker,
          taker: "0x0000000000000000000000000000000000000000",
          seriesId: BigInt(o.series_id),
          side: o.side,
          amount: BigInt(o.amount),
          price: BigInt(o.price),
          premiumToken: o.premium_token,
          minSpot: BigInt(o.min_spot),
          maxSpot: BigInt(o.max_spot),
          deadline: BigInt(o.deadline),
          nonce: BigInt(o.nonce),
        },
      ],
    });
  }

  return (
    <div className="panel" style={{ marginTop: 16 }}>
      <div className="panel-head">
        <h3>Your listed orders</h3>
      </div>
      <div className="rows">
        {mine.map((o) => (
          <div className="row" key={o.digest}>
            <div className="row-main">
              <span className="pill">Selling</span>
              <div className="row-title num">
                {fmtNum(wei(o.amount))} @ {fmtEth(wei(o.price) * 1_000_000)} / 1M
              </div>
            </div>
            <span className="row-sub mono">until {new Date(o.deadline * 1000).toLocaleString()}</span>
            <button className="btn" onClick={() => cancel(o)}>
              Cancel
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
